import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { cn, IconButton } from '@c2fo/liquidity';
import { ArrowLeftIcon, CheckIcon } from '@c2fo/liquidity/icons';

export interface DrawerSubmenuItem {
  key: string;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

interface DrawerSubmenuOverlayProps {
  isOpen: boolean;
  portalContainer: Element | null;
  shouldShowPortal: boolean;
  onBack: () => void;
  title: string;
  subtitle?: string;
  items: DrawerSubmenuItem[];
  drawerSubmenuClassName?: string;
}

const DrawerSubmenuOverlay = ({
  isOpen,
  portalContainer,
  shouldShowPortal,
  onBack,
  title,
  subtitle,
  items,
  drawerSubmenuClassName,
}: DrawerSubmenuOverlayProps) => {
  const { t } = useTranslation();

  if (!shouldShowPortal || !portalContainer) {
    return null;
  }

  return createPortal(
    <div
      className={cn('absolute inset-0 z-10 overflow-hidden', {
        'pointer-events-none': !isOpen,
      })}
    >
      <div
        className={cn(
          'absolute left-0 top-0 h-full w-full transform bg-white transition-transform duration-200 ease-in-out',
          {
            'translate-x-0': isOpen,
            'translate-x-full': !isOpen,
          },
          drawerSubmenuClassName
        )}
      >
        <div className="flex h-full w-full flex-col gap-4 p-3 px-0 pt-4">
          <div className="px-3">
            <div className="-ml-1 flex items-center gap-2">
              <IconButton
                className="size-8 [&>svg]:size-5"
                icon={ArrowLeftIcon}
                name={t('core.back')}
                onClick={onBack}
                size="sm"
                variant="ancillary"
              />
              <p className="text-sm font-bold text-text-secondary">{title}</p>
            </div>
            {subtitle && <div className="pl-9 pt-1 text-sm text-text-secondary">{subtitle}</div>}
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col">
              {items.map((item) => (
                <button
                  key={item.key}
                  className={cn(
                    'flex cursor-pointer items-center justify-between border-l-4 border-transparent py-2 pl-4 pr-5 transition-colors duration-200 hover:bg-lightBlue-50',
                    {
                      'border-lightBlue-500 bg-lightBlue-50': item.isSelected,
                    }
                  )}
                  onClick={item.onClick}
                  type="button"
                >
                  <span>{item.label}</span>
                  {item.isSelected && <CheckIcon className="size-6 fill-text-secondary/50" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalContainer
  );
};

export default DrawerSubmenuOverlay;
